import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="sticky-video"
export default class extends Controller {
  static targets = ["player", "spacer"]

  connect() {
    this.isDesktop = false
    this.playerHeight = 0
    this.isSticky = false
    
    // Bind methods to preserve context
    this.checkSticky = this.checkSticky.bind(this)
    this.handleResize = this.handleResize.bind(this)
    
    // Setup initial state with a small delay to ensure DOM is ready
    setTimeout(() => {
      this.setupInitialState()
    }, 100)
    
    // Add event listeners
    window.addEventListener('scroll', this.checkSticky)
    window.addEventListener('resize', this.handleResize)
  }

  disconnect() {
    // Clean up event listeners
    window.removeEventListener('scroll', this.checkSticky)
    window.removeEventListener('resize', this.handleResize)
  }

  setupInitialState() {
    this.updateDesktopState()
    
    if (this.isDesktop && this.hasPlayerTarget) {
      // Ensure we're not in sticky mode when calculating original position
      this.removeSticky()
      
      // Wait for DOM to settle after removing sticky
      requestAnimationFrame(() => {
        // Get the video player height including margins/padding
        const rect = this.playerTarget.getBoundingClientRect()
        const computedStyle = window.getComputedStyle(this.playerTarget)
        const marginTop = parseFloat(computedStyle.marginTop) || 0
        const marginBottom = parseFloat(computedStyle.marginBottom) || 0
        
        this.playerHeight = Math.round(rect.height + marginTop + marginBottom)
        
        // Initial check
        this.checkSticky()
      })
    }
  }

  updateDesktopState() {
    this.isDesktop = window.innerWidth >= 200 // Apply sticky behavior to all screen sizes
  }

  checkSticky() {
    if (!this.isDesktop || !this.hasPlayerTarget) {
      this.removeSticky()
      return
    }
    
    if (this.shouldPopout()) {
      this.addSticky()
    } else {
      this.removeSticky()
    }
  }

  shouldPopout() {
    const element = this.isSticky && this.hasSpacerTarget ? this.spacerTarget : this.playerTarget
    const rect = element.getBoundingClientRect()
    const topBoundary = this.viewportTopBoundary()

    const midpoint = rect.top + rect.height / 2
    return midpoint < topBoundary
  }

  viewportTopBoundary() {
    const header = document.querySelector('header.sticky')
    if (!header) return 0

    return header.getBoundingClientRect().bottom
  }

  addSticky() {
    if (this.hasPlayerTarget && !this.isSticky) {
      this.isSticky = true
      
      // Remove sticky before adding fixed
      this.playerTarget.classList.remove('sticky')
      this.playerTarget.classList.add(
        'fixed', 'bottom-4', 'right-4', 'sm:w-[400px]','w-[240px]',
        'z-50', 'shadow-2xl'
      )
      
      // Add spacer to prevent content jump
      if (this.hasSpacerTarget) {
        this.spacerTarget.style.height = `${this.playerHeight}px`
        this.spacerTarget.classList.remove('hidden')
      }
    }
  }

  removeSticky() {
    if (this.hasPlayerTarget) {
      // Only update state if we're actually sticky
      if (this.isSticky) {
        this.isSticky = false
      }
      
      // Remove fixed styles and add sticky back
      this.playerTarget.classList.remove(
        'fixed', 'bottom-4', 'right-4', 'sm:w-[400px]','w-[240px]',
        'z-50', 'shadow-2xl'
      )
      this.playerTarget.classList.add('sticky')
      
      // Hide spacer
      if (this.hasSpacerTarget) {
        this.spacerTarget.classList.add('hidden')
        this.spacerTarget.style.height = '0px'
      }
    }
  }

  handleResize() {
    const wasDesktop = this.isDesktop
    this.updateDesktopState()
    
    if (!this.isDesktop) {
      this.removeSticky()
    } else if (!wasDesktop && this.isDesktop) {
      // Switched from mobile to desktop, recalculate everything
      setTimeout(() => {
        this.setupInitialState()
      }, 100)
    }
    // That's it! No recalculation during desktop resize to prevent jumps
  }
}
